import isEmpty from 'lodash/isEmpty'
import React from 'react'
import { TextAreaInput } from '@ztechapps/webui.input'
import type { InventoryBySerialNumber } from '@ztechapps/common.contract'
import type { SerialNumberStrategy, SerialNumberInputProps, ValidationResult } from './types'

export const validateManualEntry = (input: string, maxQuantity: number): ValidationResult => {
  const codes = input
    .split(/[\n,]+/)
    .map((code) => code.trim())
    .filter((code) => !isEmpty(code))

  if (isEmpty(codes)) {
    return { isValid: true, serialNumbers: [] }
  }

  // Check for duplicates
  const uniqueCodes = new Set(codes)
  if (uniqueCodes.size !== codes.length) {
    return {
      isValid: false,
      error: 'Duplicate serial numbers detected',
      serialNumbers: [],
    }
  }

  // Validate count only if maxQuantity is not -1 (infinite)
  if (maxQuantity !== -1 && codes.length > maxQuantity) {
    return {
      isValid: false,
      error: `Too many serial numbers. Maximum allowed: ${maxQuantity}`,
      serialNumbers: [],
    }
  }

  const serialNumbers = codes.map((code) => ({ code }))
  return { isValid: true, serialNumbers }
}

export const getManualInitialValue = (serialNumbers: InventoryBySerialNumber[]): string => {
  return serialNumbers.map((v) => v.code).join('\n')
}

export const ManualEntryInput = ({ value, onChange, disabled, maxQuantity, error }: SerialNumberInputProps) => {
  const maxQtyDisplay = maxQuantity === -1 ? 'unlimited' : maxQuantity
  const enteredCount = value.split(/[\n,]+/).filter((c) => c.trim()).length

  return (
    <>
      <TextAreaInput
        rows={maxQuantity === -1 ? 7 : Math.min(maxQuantity || 5, 10)}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Enter serial numbers..."
        className="font-monospace"
        disabled={disabled}
      />

      <div className="small text-muted">
        <div>
          Enter {maxQtyDisplay} serial numbers (one per line or comma-separated). Paste multiple serial number at once.
        </div>
        <div>
          Serial numbers entered: {enteredCount} / {maxQtyDisplay}
        </div>
      </div>

      {error && <div className="text-danger small">{error}</div>}
    </>
  )
}

export const ManualStrategy: SerialNumberStrategy = {
  validator: validateManualEntry,
  getInitialValue: getManualInitialValue,
  InputComponent: ManualEntryInput,
}
